import React, { useState } from 'react';
import axios from 'axios';
import { fehlerText } from '../lib/belegKarte';
import { normalisiereArt } from '../lib/rechnungsart';
import './RechnungsartDialoge.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

interface Props {
  rechnung: { id: number; nummer: string; rechnungsart?: string | null };
  /** Erfolgsmeldung (Stornorechnung angelegt bzw. Entwurf gelöscht). */
  onFertig: (meldung: string) => void;
  onAbbrechen: () => void;
}

/** Bestätigungsdialog „Rechnung stornieren“ → POST …/storno. Das System erstellt eine Stornorechnung
    (eigener Beleg, RK-Nummernkreis); der Grund ist Pflicht. Ein Entwurf wird stattdessen gelöscht
    (Antwort `geloescht: true`). */
const StornoDialog: React.FC<Props> = ({ rechnung, onFertig, onAbbrechen }) => {
  const [grund, setGrund] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const istSchluss = normalisiereArt(rechnung.rechnungsart) === 'SCHLUSS';

  const stornieren = async () => {
    if (!grund.trim()) {
      setFehler('Bitte einen Grund für die Stornierung angeben.');
      return;
    }
    setFehler(null);
    setLaedt(true);
    try {
      const res = await axios.post(`${API_URL}/api/dokumente/ausgangsrechnungen/${rechnung.id}/storno`,
        { grund: grund.trim() });
      if (res.data?.geloescht) {
        onFertig(res.data.hinweis || `${rechnung.nummer} war noch ein Entwurf und wurde gelöscht.`);
      } else {
        const rk = res.data?.storno_rechnung?.nummer_anzeige;
        onFertig(`${rechnung.nummer} ist storniert${rk ? `; Stornorechnung ${rk} wurde erstellt` : ''}.`);
      }
    } catch (err: unknown) {
      const detail = axios.isAxiosError(err) ? err.response?.data?.detail : undefined;
      setFehler(fehlerText(detail, 'Rechnung konnte nicht storniert werden.'));
    } finally {
      setLaedt(false);
    }
  };

  return (
    <div className="rd-dialog rd-dialog--danger" role="dialog" aria-label={`${rechnung.nummer} stornieren`}>
      <h4 className="rd-titel">{rechnung.nummer} stornieren</h4>
      <div className="rd-hinweis">
        Es wird eine <strong>Stornorechnung</strong> erstellt, die diese Rechnung in vollem Umfang aufhebt;
        die Rechnung selbst erhält den Status „Storniert“. Das lässt sich nicht rückgängig machen.
        Bereits bezahlte Rechnungen lassen sich nicht stornieren – dafür eine Gutschrift erstellen.
        {istSchluss && ' Die verrechneten Abschlagsrechnungen bleiben als eigene Rechnungen bestehen.'}
      </div>
      <label className="rd-hinweis" htmlFor={`storno-grund-${rechnung.id}`}>Grund (Pflicht, wird im Protokoll festgehalten)</label>
      <textarea
        id={`storno-grund-${rechnung.id}`}
        className="rd-textarea"
        value={grund}
        onChange={e => setGrund(e.target.value)}
        placeholder="z. B. falscher Empfänger, Auftrag storniert"
        autoFocus
      />
      {fehler && <div className="rd-fehler" role="alert">{fehler}</div>}
      <div className="rd-zeile">
        <button type="button" className="k-btn k-btn--danger" onClick={stornieren} disabled={laedt || !grund.trim()}>
          {laedt ? 'Storniert…' : 'Stornorechnung erstellen'}
        </button>
        <button type="button" className="k-btn k-btn--ghost" onClick={onAbbrechen} disabled={laedt}>
          Abbrechen
        </button>
      </div>
    </div>
  );
};

export default StornoDialog;
